import { Loader, ThemeColors } from '@octopus-do/octopus-storybook'
import type { ProjectItem } from '@store/reducers/dashboard/types'
import type React from 'react'
import { Link } from 'react-router-dom'

import { ProjectStatusBadge } from '@/components/ProjectStatusMenu'
import { clsJoin } from '@/utils'
import { useProjectLink } from '../../hooks'
import { PinMarker } from '../PinMarker'
import { ProjectActionsMenu } from '../ProjectActionsMenu'
import { ScheduleMarker } from '../ScheduleMarker'
import { SharedMarker } from '../SharedMarker'
import { ProjectLabel, ProjectThumbnail } from './components'
import css from './styles.module.css'

interface Props {
  project: ProjectItem
}

// A project as a tile: the preview fills the card, the label sits under it.
export const ProjectListItem: React.FC<Props> = ({ project }) => {
  const { loading, onClick, to } = useProjectLink(project)

  const theme = project.theme as keyof typeof ThemeColors

  const isReport = project.type === 'report'

  const className = clsJoin(
    css.project_card,
    css.adaptive_card,
    isReport && css.report_card,
    css[theme],
    ThemeColors[theme],
    `--theme-${theme}`,
    loading && css.loading
  )

  return (
    <Link
      tabIndex={0}
      to={to}
      className={className}
      onClick={onClick}
      aria-busy={loading}
    >
      <ProjectThumbnail project={project} />
      <div className={css.label}>
        <ProjectLabel project={project} />
      </div>
      {project.status && (
        <div className={css.status}>
          <ProjectStatusBadge status={project.status} />
        </div>
      )}
      <div className={css.markers}>
        <PinMarker pinned={project.pinned} />
        <SharedMarker email={project.sharedBy} />
        {isReport && project.scheduleActive && (
          <ScheduleMarker
            lastCrawlAt={project.lastCrawlAt}
            nextCrawlAt={project.nextCrawlAt}
          />
        )}
      </div>
      <div className={css.actions}>
        <ProjectActionsMenu project={project} />
      </div>
      {loading && (
        <div className={css.loader}>
          <Loader color="gray" medium />
        </div>
      )}
    </Link>
  )
}
